import { createAction, createReducer } from "redux-act";
import produce from "immer";
import { INotificationItem } from "@evergis/ui";
import { generateId } from "@evergis/api";

export const reducerName = "notifications";

export interface Notification extends Omit<INotificationItem, "onCloseRequest" | "id"> {
  id?: string;
  onCloseRequest?: null;
}

export type NotificationInfo = {
  title: string;
  id?: string;
  layer?: string;
  table?: string;
  duration?: number | null;
  description?: string | JSX.Element;
  warning?: boolean;
  error?: boolean;
  success?: boolean;
  button?: string;
  progress?: boolean | number;
  onButtonClick?: VoidFunction;
};

export type NotificationPayload = {
  taskId?: string;
  info?: NotificationInfo;
  success?: NotificationInfo;
  fail?: NotificationInfo;
  cancel?: NotificationInfo;
  manualDeleteOnFail?: boolean;
};

export type NotificationsState = {
  messages: Notification[];
  notificationPayload?: NotificationPayload;
};

const initialState: NotificationsState = {
  messages: [],
};

export const reducer = createReducer({}, initialState);
export const setNotificationPayload = createAction<NotificationPayload>("notification/set-notification-payload");
export const resetNotificationPayload = createAction("notification/reset-notification-payload");
export const addNotification = createAction<Notification>("notification/add-notification");
export const notifyError = (notification: Notification) => addNotification({ error: true, ...notification });
export const notifyWarn = (notification: Notification) => addNotification({ warning: true, ...notification });
export const notifySuccess = (notification: Notification) => addNotification({ success: true, ...notification });
export const closeNotification = createAction<string>("notification/close-notification");

reducer.on(
  addNotification,
  produce((state: NotificationsState, message: Notification) => {
    if (message.id === void 0) {
      message.id = generateId();
    }
    state.messages.push(message);
  }),
);

reducer.on(
  setNotificationPayload,
  produce((state: NotificationsState, payload: NotificationPayload) => {
    state.notificationPayload = payload;
  }),
);

reducer.on(
  closeNotification,
  produce((state: NotificationsState, id: string) => {
    state.messages = state.messages.filter(item => item.id !== id);
  }),
);

reducer.on(
  resetNotificationPayload,
  produce((state: NotificationsState) => {
    state.notificationPayload = null;
  }),
);
